/**
 * <load-more-products>
 *
 * Progressive-enhancement pagination for product listings (collection / search).
 * Fetches the next paginated page through the Section Rendering API, extracts the
 * <product-list> children and appends them to the existing grid.
 *
 * Modes:
 *   load_more  -> button only, user clicks each time
 *   infinite   -> auto-loads when the element scrolls into view. After `auto-pages`
 *                 consecutive auto-loads it falls back to the button, so the footer
 *                 stays reachable (this is the accessibility/SEO safe pattern).
 *
 * The element is fully self-contained: no dependency on theme.js internals, so it
 * survives theme updates. It re-initialises automatically after a facet update
 * because the whole section (and therefore this element) is replaced.
 */
(() => {
  if (window.customElements.get('load-more-products')) {
    return;
  }

  class LoadMoreProducts extends HTMLElement {
    connectedCallback() {
      this.mode = this.getAttribute('mode') || 'load_more';
      this.autoPages = parseInt(this.getAttribute('auto-pages') || '0', 10);
      this.sectionId = this.getAttribute('section-id');

      this.button = this.querySelector('.load-more__button, [data-load-more-button]');
      this.status = this.querySelector('[data-load-more-status]');
      this.progress = this.querySelector('[data-load-more-progress]');
      this.announcer = this.querySelector('[data-load-more-announcer]');

      this.isLoading = false;
      this.autoLoadedCount = 0;

      // Graceful degradation: without IntersectionObserver, infinite scroll
      // becomes a plain "Load more" button.
      if (this.mode === 'infinite' && !('IntersectionObserver' in window)) {
        this.mode = 'load_more';
      }

      this.button?.addEventListener('click', this.onButtonClick.bind(this));

      this.updateStatus();
      this.startObserving();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    /* ------------------------------------------------------------------ */

    get sectionRoot() {
      return this.closest('.shopify-section') || document.getElementById(`shopify-section-${this.sectionId}`);
    }

    get productList() {
      return this.sectionRoot?.querySelector('product-list');
    }

    get nextUrl() {
      return this.getAttribute('next-url');
    }

    get totalCount() {
      return parseInt(this.getAttribute('total-count') || '0', 10);
    }

    /**
     * Products that precede the first card currently in the grid. Non-zero only on
     * a deep-linked page (?page=3), where the grid starts part-way through the
     * collection. Keeps the "showing X of Y" counter honest.
     */
    get startOffset() {
      return parseInt(this.getAttribute('start-offset') || '0', 10);
    }

    get loadedCount() {
      return this.startOffset + (this.productList?.querySelectorAll('product-card').length || 0);
    }

    /* ------------------------------------------------------------------ */

    startObserving() {
      if (this.mode !== 'infinite' || !this.nextUrl || !('IntersectionObserver' in window)) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          if (this.autoPages > 0 && this.autoLoadedCount >= this.autoPages) return;
          this.load({ auto: true });
        },
        { rootMargin: '800px 0px 800px 0px' }
      );

      this.observer.observe(this);
    }

    /**
     * IntersectionObserver only fires on threshold *crossings*. If the sentinel is
     * still inside the trigger zone after appending a page (short pages, tall
     * viewports), no further callback ever arrives and pagination stalls. So after
     * every load we re-check the geometry ourselves. The auto-page budget caps how
     * far this can run.
     */
    maybeContinue() {
      if (this.mode !== 'infinite' || !this.nextUrl) return;
      if (this.autoPages > 0 && this.autoLoadedCount >= this.autoPages) return;

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const rect = this.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 800) {
          this.load({ auto: true });
        }
      });
    }

    onButtonClick(event) {
      event.preventDefault();
      // A manual click resets the auto-load budget, so infinite mode resumes.
      this.autoLoadedCount = 0;
      this.load({ auto: false });
    }

    /* ------------------------------------------------------------------ */

    async load({ auto = false } = {}) {
      if (this.isLoading || !this.nextUrl) return;

      this.isLoading = true;
      if (auto) this.autoLoadedCount += 1;

      this.toggleAttribute('loading', true);
      this.button?.setAttribute('aria-busy', 'true');
      this.removeAttribute('error');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const url = new URL(this.nextUrl, window.location.origin);
      url.hash = '';
      url.searchParams.set('section_id', this.sectionId);

      try {
        this.abortController = new AbortController();

        const response = await fetch(url.toString(), { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Failed to load page: ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const firstNewCard = this.appendProducts(doc);

        this.syncState(doc);
        this.updateStatus();

        // Move focus to the first newly added card when the user explicitly asked
        // for more products. In infinite mode we never steal focus.
        if (!auto && firstNewCard) {
          firstNewCard.setAttribute('tabindex', '-1');
          firstNewCard.focus({ preventScroll: true });
        }

        this.maybeContinue();

        this.dispatchEvent(
          new CustomEvent('load-more:loaded', {
            bubbles: true,
            detail: { loadedCount: this.loadedCount, totalCount: this.totalCount, auto }
          })
        );
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[load-more-products]', error);
        this.toggleAttribute('error', true);
      } finally {
        this.isLoading = false;
        this.toggleAttribute('loading', false);
        this.button?.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    /**
     * Moves the product cards (and promo blocks) from the fetched document into
     * the live grid. Returns the first inserted element.
     */
    appendProducts(doc) {
      const incomingList = doc.querySelector('product-list');
      const currentList = this.productList;

      if (!incomingList || !currentList) return null;

      const fragment = document.createDocumentFragment();

      Array.from(incomingList.children).forEach((child) => {
        const node = document.importNode(child, true);

        // `reveal-on-scroll` sets opacity:0 and is only animated by <product-list>
        // on its initial connectedCallback. Appended nodes would stay invisible,
        // so we strip the attribute and use our own fade-in instead.
        node.removeAttribute('reveal-on-scroll');
        node.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        node.classList.add('is-appended');

        fragment.appendChild(node);
      });

      const firstNewCard = fragment.firstElementChild;
      currentList.appendChild(fragment);

      return firstNewCard;
    }

    /**
     * Copies the pagination state from the fetched document onto this element, so
     * the next click/scroll knows where to go.
     */
    syncState(doc) {
      const incomingControl = doc.querySelector('load-more-products');

      // Primary source: the control rendered by the fetched page. Fallback: the
      // rel="next" link in the numbered pagination, which is always present in the
      // markup. Without the fallback, any change that stops the control from being
      // rendered would silently end pagination after a single page.
      const nextUrl =
        incomingControl?.getAttribute('next-url') ||
        doc.querySelector('.pagination a[rel="next"]')?.getAttribute('href') ||
        null;

      if (nextUrl) {
        this.setAttribute('next-url', nextUrl.split('#')[0]);
        this.setAttribute('current-page', incomingControl?.getAttribute('current-page') || '');
      } else {
        this.removeAttribute('next-url');
        this.observer?.disconnect();
      }
    }

    /**
     * In "infinite" mode the button is only surfaced once the auto-load budget is
     * spent, so the user regains control (and can reach the footer).
     */
    get shouldShowButton() {
      if (!this.nextUrl) return false;
      if (this.mode !== 'infinite') return true;
      if (this.autoPages <= 0) return false;
      return this.autoLoadedCount >= this.autoPages;
    }

    updateStatus() {
      const loaded = this.loadedCount;
      const total = this.totalCount;

      this.toggleAttribute('complete', !this.nextUrl);
      this.toggleAttribute('show-button', this.shouldShowButton);

      if (this.status) {
        const template = this.status.getAttribute('data-template') || '';
        this.status.textContent = template.replace('[loaded]', loaded).replace('[total]', total);
      }

      if (this.progress) {
        const ratio = total > 0 ? Math.min(loaded / total, 1) : 1;
        this.progress.style.setProperty('--load-more-progress', ratio);
        this.progress.setAttribute('aria-valuenow', String(loaded));
        this.progress.setAttribute('aria-valuemax', String(total));
      }

      if (this.announcer) {
        this.announcer.textContent = `${loaded} / ${total}`;
      }
    }
  }

  window.customElements.define('load-more-products', LoadMoreProducts);
})();